import { ArrowLeft, ArrowRight, CheckCircle2, Sparkles } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CloudinaryImageField } from "@/components/admin/CloudinaryImageField";
import { Input } from "@/components/ui/input";
import { authConfig, api, formatApiErrorDetail } from "@/lib/api";

const CATEGORY_OPTIONS = [
  { value: "buy", label: "Buy" },
  { value: "rent", label: "Rent" },
  { value: "new_launch", label: "New Launch" },
];

const RESPONSE_SLA_OPTIONS = [
  "Replies within 15 minutes during working hours",
  "Replies within 1 hour during working hours",
  "Same-day callback for every qualified enquiry",
  "Concierge-first response, advisor follow-up shortly after",
];

const emptyForm = {
  name: "",
  phone: "",
  subdomain_slug: "",
  cea_registration_number: "",
  parent_agency_name: "",
  agency_license_number: "",
  profile_photo_url: "",
  team_name: "",
  team_logo_url: "",
  advisor_tagline: "",
  advisor_bio: "",
  specialties: "",
  service_regions: "",
  preferred_categories: ["buy", "new_launch"],
  response_sla: RESPONSE_SLA_OPTIONS[0],
};

const toCommaString = (value = []) => (Array.isArray(value) ? value.join(", ") : "");
const toList = (value = "") => String(value || "").split(",").map((item) => item.trim()).filter(Boolean);

export default function AgentWorkspaceOnboardingWizardPage({ onSessionRefresh }) {
  const navigate = useNavigate();
  const { agentSlug } = useParams();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [wizard, setWizard] = useState(null);
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(emptyForm);

  const successBannerVisible = searchParams.get("billing_status") === "success";

  const hydrateForm = (payload) => ({
    name: payload?.name || "",
    phone: payload?.phone || "",
    subdomain_slug: payload?.subdomain_slug || "",
    cea_registration_number: payload?.cea_registration_number || "",
    parent_agency_name: payload?.parent_agency_name || "",
    agency_license_number: payload?.agency_license_number || "",
    profile_photo_url: payload?.profile_photo_url || "",
    team_name: payload?.team_name || "",
    team_logo_url: payload?.team_logo_url || "",
    advisor_tagline: payload?.advisor_tagline || "",
    advisor_bio: payload?.advisor_bio || "",
    specialties: toCommaString(payload?.specialties),
    service_regions: toCommaString(payload?.service_regions),
    preferred_categories: payload?.preferred_categories?.length ? payload.preferred_categories : ["buy", "new_launch"],
    response_sla: payload?.response_sla || RESPONSE_SLA_OPTIONS[0],
  });

  useEffect(() => {
    let cancelled = false;
    const loadWizard = async () => {
      setLoading(true);
      try {
        const response = await api.get("/onboarding/wizard", authConfig());
        if (cancelled) return;
        setWizard(response.data);
        setForm(hydrateForm(response.data));
        setStep(response.data.onboarding_step || 1);
        if (response.data.onboarding_completed_at) {
          navigate(response.data.dashboard_path || `/agent/${agentSlug}/dashboard`, { replace: true });
        }
      } catch (error) {
        if (!cancelled) toast.error(formatApiErrorDetail(error.response?.data?.detail));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    loadWizard();
    return () => {
      cancelled = true;
    };
  }, [agentSlug, navigate]);

  const progressSteps = useMemo(
    () => [
      { number: 1, label: "Identity & Brand" },
      { number: 2, label: "Market Positioning" },
      { number: 3, label: "Service Settings" },
      { number: 4, label: "Launch Readiness" },
    ],
    [],
  );

  const updateField = (key, value) => setForm((current) => ({ ...current, [key]: value }));

  const buildPayload = (nextStep = step) => ({
    name: form.name,
    phone: form.phone,
    subdomain_slug: form.subdomain_slug,
    cea_registration_number: form.cea_registration_number,
    parent_agency_name: form.parent_agency_name,
    agency_license_number: form.agency_license_number,
    profile_photo_url: form.profile_photo_url,
    team_name: form.team_name,
    team_logo_url: form.team_logo_url,
    advisor_tagline: form.advisor_tagline,
    advisor_bio: form.advisor_bio,
    specialties: toList(form.specialties),
    service_regions: toList(form.service_regions),
    preferred_categories: form.preferred_categories,
    response_sla: form.response_sla,
    onboarding_step: nextStep,
  });

  const validateStep = () => {
    if (step === 1 && (!form.subdomain_slug.trim() || !form.name.trim() || !form.phone.trim() || !form.cea_registration_number.trim() || !form.parent_agency_name.trim() || !form.agency_license_number.trim() || !form.profile_photo_url.trim())) {
      return "Complete your slug, name, phone, CEA, agency, license, and headshot before continuing.";
    }
    if (step === 1 && !form.advisor_tagline.trim()) return "Add a public-facing advisor tagline before continuing.";
    if (step === 2 && (!form.advisor_bio.trim() || !toList(form.specialties).length || !toList(form.service_regions).length)) {
      return "Add your advisor bio, specialties, and service regions before continuing.";
    }
    if (step === 3 && (!form.response_sla || !form.preferred_categories.length)) {
      return "Set a response expectation and at least one preferred category before continuing.";
    }
    return null;
  };

  const saveWizard = async (nextStep = step, message = "Draft saved") => {
    setSaving(true);
    try {
      const response = await api.put("/onboarding/wizard", buildPayload(nextStep), authConfig());
      setWizard(response.data);
      setForm(hydrateForm(response.data));
      setStep(nextStep);
      await onSessionRefresh?.();
      toast.success(message);
      return response.data;
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
      return null;
    } finally {
      setSaving(false);
    }
  };

  const handleNext = async () => {
    const validationError = validateStep();
    if (validationError) {
      toast.error(validationError);
      return;
    }
    await saveWizard(Math.min(step + 1, 4), "Step saved");
  };

  const handleComplete = async () => {
    const validationError = step === 4 ? null : validateStep();
    if (validationError) {
      toast.error(validationError);
      return;
    }
    setCompleting(true);
    try {
      await saveWizard(4, "Launch settings saved");
      const response = await api.post("/onboarding/wizard/complete", {}, authConfig());
      setWizard(response.data);
      await onSessionRefresh?.();
      toast.success("Workspace setup complete");
      navigate(response.data.dashboard_path || `/agent/${agentSlug}/dashboard`, { replace: true });
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
    } finally {
      setCompleting(false);
    }
  };

  if (loading) {
    return <div className="flex min-h-screen items-center justify-center bg-[#f5efe2] text-[#001935]" data-testid="agent-workspace-onboarding-loading">Loading your launch wizard...</div>;
  }

  return (
    <div className="min-h-screen overflow-x-hidden bg-[linear-gradient(180deg,#f7f0e1_0%,#f4ecde_36%,#efe5d7_100%)] px-4 py-8 text-[#001935] sm:px-6 lg:px-10" data-testid="agent-workspace-onboarding-page">
      <div className="mx-auto grid max-w-7xl gap-6 lg:grid-cols-[0.72fr_0.28fr]">
        <div className="min-w-0 space-y-6">
          <section className="rounded-[32px] border border-[#D4AF37]/25 bg-white/85 p-6 shadow-[0_24px_70px_rgba(90,75,53,0.08)] md:p-8" data-testid="agent-workspace-onboarding-hero">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="max-w-3xl">
                <p className="text-xs font-bold uppercase tracking-[0.24em] text-[#8b6d16]">Post-subscription setup wizard</p>
                <h1 className="mt-3 font-[Cormorant_Garamond,serif] text-4xl font-bold leading-none text-[#2C2C2C] md:text-5xl">Launch your portal before you enter the workspace.</h1>
                <p className="mt-4 text-sm leading-8 text-[#42566f]">Set your profile, market positioning, and response rails now so the portal feels credible the moment buyers land on it.</p>
              </div>
              <div className="rounded-[24px] border border-[#D4AF37]/22 bg-[#fff8ea] px-5 py-4 text-sm leading-7 text-[#6b5c2d]" data-testid="agent-workspace-onboarding-summary-card">
                <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#8b6d16]">Portal URL</p>
                <p className="mt-2 break-all font-semibold">{typeof window !== "undefined" ? `${window.location.origin}${wizard?.public_portal_path || `/agent/${agentSlug}`}` : wizard?.public_portal_path}</p>
                <p className="mt-3 text-xs uppercase tracking-[0.16em] text-[#8b6d16]">Plan • {wizard?.plan_type}</p>
              </div>
            </div>

            {successBannerVisible ? <div className="mt-6 rounded-[24px] border border-emerald-200 bg-emerald-50 px-5 py-4 text-sm leading-7 text-emerald-900" data-testid="agent-workspace-onboarding-success-banner">
              <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.18em] text-emerald-700"><Sparkles className="h-4 w-4" /> Subscription confirmed</div>
              <p className="mt-2">Your payment is complete. Finish this guided setup and then enter the agent workspace with your portal details already in place.</p>
            </div> : null}

            <div className="mt-6 grid gap-3 md:grid-cols-4" data-testid="agent-workspace-onboarding-steps-grid">
              {progressSteps.map((item) => (
                <div className={`rounded-[22px] border px-4 py-4 ${step >= item.number ? "border-[#D4AF37] bg-[#fff4dd]" : "border-[#001935]/10 bg-[#f5efe2]"}`} data-testid={`agent-workspace-onboarding-step-${item.number}`} key={item.number}>
                  <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-[#8b6d16]">Step {item.number}</p>
                  <p className="mt-2 text-sm font-semibold text-[#2C2C2C]">{item.label}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="rounded-[32px] border border-[#001935]/10 bg-white/85 p-6 shadow-[0_24px_70px_rgba(90,75,53,0.08)] md:p-8" data-testid="agent-workspace-onboarding-form-card">
            {step === 1 ? (
              <div className="space-y-6">
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#8b6d16]">Step 1</p>
                  <h2 className="mt-2 font-[Cormorant_Garamond,serif] text-3xl font-bold text-[#2C2C2C]">Identity & brand</h2>
                  <p className="mt-3 text-sm leading-7 text-[#42566f]">Give buyers a credible first impression with a headshot, brand asset, and one-line promise.</p>
                </div>

                <div className="grid gap-6 lg:grid-cols-2">
                  <CloudinaryImageField compact folder="sgpropportal/onboarding/profile" helperText="Upload a clean, front-facing image for your portal profile." label="Profile photo" onChange={(value) => updateField("profile_photo_url", value)} previewAlt="Profile photo" testIdPrefix="agent-workspace-profile-photo" value={form.profile_photo_url} />
                  <CloudinaryImageField compact folder="sgpropportal/onboarding/logo" helperText="Optional, but useful if you operate with a team brand or boutique identity." label="Team logo" onChange={(value) => updateField("team_logo_url", value)} previewAlt="Team logo" previewMode="contain" testIdPrefix="agent-workspace-team-logo" value={form.team_logo_url} />
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <label className="grid gap-2" data-testid="agent-workspace-slug-field">
                    <span className="text-xs font-bold uppercase tracking-[0.16em] text-[#8b6d16]">Custom URL slug</span>
                    <Input data-testid="agent-workspace-slug-input" onChange={(event) => updateField("subdomain_slug", event.target.value)} placeholder="Example: dennis" value={form.subdomain_slug} />
                  </label>
                  <label className="grid gap-2" data-testid="agent-workspace-full-name-field">
                    <span className="text-xs font-bold uppercase tracking-[0.16em] text-[#8b6d16]">Full Name</span>
                    <Input data-testid="agent-workspace-full-name-input" onChange={(event) => updateField("name", event.target.value)} placeholder="Your full name" value={form.name} />
                  </label>
                  <label className="grid gap-2" data-testid="agent-workspace-contact-number-field">
                    <span className="text-xs font-bold uppercase tracking-[0.16em] text-[#8b6d16]">Contact Number</span>
                    <Input data-testid="agent-workspace-contact-number-input" onChange={(event) => updateField("phone", event.target.value)} placeholder="+65 ..." value={form.phone} />
                  </label>
                  <label className="grid gap-2" data-testid="agent-workspace-cea-registration-field">
                    <span className="text-xs font-bold uppercase tracking-[0.16em] text-[#8b6d16]">CEA Registration Number</span>
                    <Input data-testid="agent-workspace-cea-registration-input" onChange={(event) => updateField("cea_registration_number", event.target.value.toUpperCase())} placeholder="R123456A" value={form.cea_registration_number} />
                  </label>
                  <label className="grid gap-2" data-testid="agent-workspace-parent-agency-field">
                    <span className="text-xs font-bold uppercase tracking-[0.16em] text-[#8b6d16]">Parent Agency Name</span>
                    <Input data-testid="agent-workspace-parent-agency-input" onChange={(event) => updateField("parent_agency_name", event.target.value)} placeholder="Huttons Asia" value={form.parent_agency_name} />
                  </label>
                  <label className="grid gap-2" data-testid="agent-workspace-agency-license-field">
                    <span className="text-xs font-bold uppercase tracking-[0.16em] text-[#8b6d16]">Agency License Number</span>
                    <Input data-testid="agent-workspace-agency-license-input" onChange={(event) => updateField("agency_license_number", event.target.value.toUpperCase())} placeholder="L3000001A" value={form.agency_license_number} />
                  </label>
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <label className="grid gap-2" data-testid="agent-workspace-team-name-field">
                    <span className="text-xs font-bold uppercase tracking-[0.16em] text-[#8b6d16]">Team / brand name</span>
                    <input className="rounded-[18px] border border-[#001935]/10 bg-[#fffaf1] px-4 py-3 text-sm text-[#001935] outline-none transition focus:border-[#D4AF37]" data-testid="agent-workspace-team-name-input" onChange={(event) => updateField("team_name", event.target.value)} placeholder="Example: Clive Lim Signature Desk" value={form.team_name} />
                  </label>
                  <label className="grid gap-2" data-testid="agent-workspace-tagline-field">
                    <span className="text-xs font-bold uppercase tracking-[0.16em] text-[#8b6d16]">Advisor tagline</span>
                    <input className="rounded-[18px] border border-[#001935]/10 bg-[#fffaf1] px-4 py-3 text-sm text-[#001935] outline-none transition focus:border-[#D4AF37]" data-testid="agent-workspace-tagline-input" onChange={(event) => updateField("advisor_tagline", event.target.value)} placeholder="Example: District 15 family homes and value-led Singapore acquisitions" value={form.advisor_tagline} />
                  </label>
                </div>
              </div>
            ) : null}

            {step === 2 ? (
              <div className="space-y-6">
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#8b6d16]">Step 2</p>
                  <h2 className="mt-2 font-[Cormorant_Garamond,serif] text-3xl font-bold text-[#2C2C2C]">Market positioning</h2>
                  <p className="mt-3 text-sm leading-7 text-[#42566f]">Clarify the story buyers should understand about your desk before they enquire.</p>
                </div>

                <label className="grid gap-2" data-testid="agent-workspace-bio-field">
                  <span className="text-xs font-bold uppercase tracking-[0.16em] text-[#8b6d16]">Advisor bio</span>
                  <textarea className="min-h-[180px] rounded-[24px] border border-[#001935]/10 bg-[#fffaf1] px-4 py-4 text-sm leading-7 text-[#001935] outline-none transition focus:border-[#D4AF37]" data-testid="agent-workspace-bio-input" onChange={(event) => updateField("advisor_bio", event.target.value)} placeholder="Explain how you work, who you help, and the decisions you are trusted to guide." value={form.advisor_bio} />
                </label>

                <div className="grid gap-4 md:grid-cols-2">
                  <label className="grid gap-2" data-testid="agent-workspace-specialties-field">
                    <span className="text-xs font-bold uppercase tracking-[0.16em] text-[#8b6d16]">Specialties</span>
                    <input className="rounded-[18px] border border-[#001935]/10 bg-[#fffaf1] px-4 py-3 text-sm text-[#001935] outline-none transition focus:border-[#D4AF37]" data-testid="agent-workspace-specialties-input" onChange={(event) => updateField("specialties", event.target.value)} placeholder="Comma-separated: New launches, asset progression, district upgrades" value={form.specialties} />
                  </label>
                  <label className="grid gap-2" data-testid="agent-workspace-service-regions-field">
                    <span className="text-xs font-bold uppercase tracking-[0.16em] text-[#8b6d16]">Service regions</span>
                    <input className="rounded-[18px] border border-[#001935]/10 bg-[#fffaf1] px-4 py-3 text-sm text-[#001935] outline-none transition focus:border-[#D4AF37]" data-testid="agent-workspace-service-regions-input" onChange={(event) => updateField("service_regions", event.target.value)} placeholder="Comma-separated: D15, East Coast, Katong, city-fringe condos" value={form.service_regions} />
                  </label>
                </div>
              </div>
            ) : null}

            {step === 3 ? (
              <div className="space-y-6">
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#8b6d16]">Step 3</p>
                  <h2 className="mt-2 font-[Cormorant_Garamond,serif] text-3xl font-bold text-[#2C2C2C]">Service settings</h2>
                  <p className="mt-3 text-sm leading-7 text-[#42566f]">Set the response expectation and the inventory focus buyers should expect from this portal.</p>
                </div>

                <label className="grid gap-2" data-testid="agent-workspace-response-sla-field">
                  <span className="text-xs font-bold uppercase tracking-[0.16em] text-[#8b6d16]">Response expectation</span>
                  <select className="rounded-[18px] border border-[#001935]/10 bg-[#fffaf1] px-4 py-3 text-sm text-[#001935] outline-none transition focus:border-[#D4AF37]" data-testid="agent-workspace-response-sla-select" onChange={(event) => updateField("response_sla", event.target.value)} value={form.response_sla}>
                    {RESPONSE_SLA_OPTIONS.map((option) => <option key={option} value={option}>{option}</option>)}
                  </select>
                </label>

                <div className="grid gap-3" data-testid="agent-workspace-preferred-categories-group">
                  <span className="text-xs font-bold uppercase tracking-[0.16em] text-[#8b6d16]">Preferred categories</span>
                  <div className="flex flex-wrap gap-3">
                    {CATEGORY_OPTIONS.map((option) => {
                      const active = form.preferred_categories.includes(option.value);
                      return (
                        <button
                          className={`rounded-full border px-4 py-3 text-sm font-semibold transition ${active ? "border-[#D4AF37] bg-[#fff4dd] text-[#8b6d16]" : "border-[#001935]/10 bg-white text-[#42566f] hover:border-[#D4AF37]/45"}`}
                          data-testid={`agent-workspace-category-${option.value}`}
                          key={option.value}
                          onClick={() => updateField("preferred_categories", active ? form.preferred_categories.filter((item) => item !== option.value) : [...form.preferred_categories, option.value])}
                          type="button"
                        >
                          {option.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            ) : null}

            {step === 4 ? (
              <div className="space-y-6">
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#8b6d16]">Step 4</p>
                  <h2 className="mt-2 font-[Cormorant_Garamond,serif] text-3xl font-bold text-[#2C2C2C]">Launch readiness</h2>
                  <p className="mt-3 text-sm leading-7 text-[#42566f]">Review what is ready now and what can be completed right after you enter the workspace.</p>
                </div>
                <div className="grid gap-4 md:grid-cols-2" data-testid="agent-workspace-readiness-grid">
                  {(wizard?.readiness_items || []).map((item) => (
                    <div className={`rounded-[24px] border px-5 py-5 ${item.completed ? "border-emerald-200 bg-emerald-50" : "border-[#D4AF37]/25 bg-[#fffaf1]"}`} data-testid={`agent-workspace-readiness-item-${item.key}`} key={item.key}>
                      <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#8b6d16]">{item.label}</p>
                      <p className="mt-3 text-lg font-semibold text-[#2C2C2C]">{item.completed ? "Ready" : "Needs attention"}</p>
                      <p className="mt-3 text-sm leading-7 text-[#42566f]">{item.detail}</p>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}

            <div className="mt-8 flex flex-col gap-3 border-t border-[#001935]/10 pt-6 sm:flex-row sm:items-center sm:justify-between">
              <button className="outline-button !w-full !px-4 !py-2 sm:!w-auto" data-testid="agent-workspace-save-draft-button" disabled={saving || completing} onClick={() => saveWizard(step, "Draft saved")} type="button">Save Draft</button>
              <div className="flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:flex-wrap">
                {step > 1 ? <button className="outline-button !w-full !px-4 !py-2 sm:!w-auto" data-testid="agent-workspace-back-button" disabled={saving || completing} onClick={() => setStep((current) => Math.max(1, current - 1))} type="button"><ArrowLeft className="mr-2 h-4 w-4" /> Back</button> : null}
                {step < 4 ? <button className="gold-button !w-full !px-5 !py-3 sm:!w-auto" data-testid="agent-workspace-next-button" disabled={saving || completing} onClick={handleNext} type="button">{saving ? "Saving..." : <>Next <ArrowRight className="ml-2 h-4 w-4" /></>}</button> : <button className="gold-button !w-full !px-5 !py-3 sm:!w-auto" data-testid="agent-workspace-complete-button" disabled={saving || completing} onClick={handleComplete} type="button">{completing ? "Finishing setup..." : <>Finish Setup <CheckCircle2 className="ml-2 h-4 w-4" /></>}</button>}
              </div>
            </div>
          </section>
        </div>

        <aside className="min-w-0 space-y-6">
          <section className="rounded-[32px] border border-[#001935]/10 bg-[#1A1A1A] p-6 text-[#F6F1E9] shadow-[0_24px_70px_rgba(0,0,0,0.18)]" data-testid="agent-workspace-onboarding-sidebar">
            <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#D4AF37]">Launch score</p>
            <p className="mt-3 font-[Cormorant_Garamond,serif] text-5xl font-bold">{wizard?.readiness_score || 0}%</p>
            <p className="mt-3 text-sm leading-7 text-[#DDD6C9]">This score updates as you save each step. Listings can still be synced after setup, so launch is not blocked by an empty portfolio.</p>
            <div className="mt-5 rounded-[24px] border border-white/10 bg-white/5 px-4 py-4 text-sm leading-7 text-[#DDD6C9]" data-testid="agent-workspace-onboarding-sidebar-listings">
              <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-[#D4AF37]">Current live listings</p>
              <p className="mt-2 text-2xl font-semibold text-[#F6F1E9]">{wizard?.listing_count || 0}</p>
            </div>
            <div className="mt-5 rounded-[24px] border border-white/10 bg-white/5 px-4 py-4 text-sm leading-7 text-[#DDD6C9]" data-testid="agent-workspace-onboarding-sidebar-next">
              <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-[#D4AF37]">What happens next</p>
              <p className="mt-2">After completion, you land in the workspace dashboard where you can sync listings, review leads, and continue launch tasks.</p>
            </div>
            <div className="mt-5 rounded-[24px] border border-[#D4AF37]/22 bg-[#fff8ea] px-4 py-4 text-sm leading-7 text-[#3f3420]" data-testid="agent-workspace-onboarding-manual-cta-card">
              <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-[#8b6d16]">Portal guide</p>
              <p className="mt-2">Need the full post-subscription walkthrough? Open the in-app manual to see the entire portal journey you will follow after setup.</p>
              <Link className="gold-button mt-4 !w-full !justify-center" data-testid="agent-workspace-open-manual-link" to="/agent-manual">
                Open Full Agent Manual
              </Link>
            </div>
          </section>
        </aside>
      </div>
    </div>
  );
}